import { Component, OnChanges, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import {
  ModuleSurfaceOperatorConfig,
  WORKFLOW_DESIGNER_MODULE_SURFACE_MODULE_KEYS,
  defaultModuleSurfaceOperatorConfig
} from '../workflow-catalog.models';
import { WorkflowModuleSurfaceRegistry } from '../../../shared/workflow-surfaces/workflow-module-surface.registry';
import { WorkflowVariableMappingGridComponent } from './workflow-variable-mapping-grid.component';
import {
  buildModuleSurfaceConfigurationJson,
  parseModuleSurfaceOperatorConfig
} from './workflow-module-surface.designer.utils';

@Component({
  standalone: true,
  selector: 'ain-workflow-module-surface-operator-panel',
  imports: [FormsModule, SelectModule, InputTextModule, CheckboxModule, WorkflowVariableMappingGridComponent],
  template: `
    <section class="operator-panel">
      <h5>{{ t('workflow.designer.moduleSurface.title') }}</h5>

      <label>{{ t('workflow.designer.moduleSurface.moduleKey') }}</label>
      <p-select
        [options]="moduleOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.moduleKey"
        (ngModelChange)="onModuleKeyChanged()"
        [editable]="true"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      <label>{{ t('workflow.designer.moduleSurface.surfaceKey') }}</label>
      <p-select
        [options]="surfaceOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.surfaceKey"
        (ngModelChange)="onSurfaceChanged()"
        [editable]="true"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      <h6>{{ t('workflow.designer.moduleSurface.contextMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.contextMapping"
        (mappingChange)="onContextMappingChanged($event)" />

      <label>
        <p-checkbox [(ngModel)]="model.allowFullPageEscape" (ngModelChange)="emitConfig()" [binary]="true" />
        {{ t('workflow.designer.moduleSurface.allowFullPageEscape') }}
      </label>

      @if (model.allowFullPageEscape) {
        <label>{{ t('workflow.designer.moduleSurface.fullPageRouteTemplate') }}</label>
        <input
          pInputText
          class="w-full"
          [(ngModel)]="model.fullPageRouteTemplate"
          (ngModelChange)="emitConfig()"
          [placeholder]="t('workflow.designer.moduleSurface.fullPageRoutePlaceholder')" />
      }

      @if (previewText()) {
        <p class="preview">{{ previewText() }}</p>
      }

      @if (surfaceWarning()) {
        <p class="warning">{{ surfaceWarning() }}</p>
      }
    </section>
  `,
  styles: [`
    .operator-panel { display: grid; gap: 8px; margin-top: 8px; }
    .operator-panel h5, .operator-panel h6 { margin: 0; }
    .w-full { width: 100%; }
    .preview { margin: 0; padding: 8px; border-radius: 8px; background: #eff6ff; color: #1e3a8a; font-size: 13px; }
    .warning { margin: 0; padding: 8px; border-radius: 8px; background: #fffbeb; color: #92400e; font-size: 12px; }
  `]
})
export class WorkflowModuleSurfaceOperatorPanelComponent implements OnChanges {
  private readonly localization = inject(LocalizationService);
  private readonly surfaceRegistry = inject(WorkflowModuleSurfaceRegistry);

  readonly configJson = input('');
  readonly configJsonChange = output<string>();

  model: ModuleSurfaceOperatorConfig = defaultModuleSurfaceOperatorConfig();
  readonly surfaceWarning = signal<string | null>(null);
  /** Tracks module key for surface dropdown; `computed` cannot depend on plain `model` fields. */
  readonly selectedModuleKey = signal('');

  readonly moduleOptions = computed(() => {
    const keys = new Set<string>(WORKFLOW_DESIGNER_MODULE_SURFACE_MODULE_KEYS);
    for (const entry of this.surfaceRegistry.listAll()) {
      keys.add(entry.moduleKey);
    }
    return [...keys]
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({ label: value, value }));
  });

  readonly surfaceOptions = computed(() => {
    const moduleKey = this.selectedModuleKey().trim().toLowerCase();
    if (!moduleKey) {
      return [];
    }

    const options = this.surfaceRegistry.listByModule(moduleKey)
      .map(entry => {
        const translatedTitle = this.t(entry.titleKey);
        const title = translatedTitle === entry.titleKey ? entry.surfaceKey : translatedTitle;
        return {
          // Show surfaceKey in label so similarly named route-based vs custom surfaces stay distinguishable.
          label: `${title} (${entry.surfaceKey})`,
          value: entry.surfaceKey
        };
      });

    // Guard against duplicate value collisions in dropdown optionValue.
    const deduped = new Map<string, { label: string; value: string }>();
    for (const option of options) {
      if (!deduped.has(option.value)) {
        deduped.set(option.value, option);
      }
    }

    return [...deduped.values()].sort((a, b) => a.label.localeCompare(b.label));
  });

  ngOnChanges(): void {
    this.model = parseModuleSurfaceOperatorConfig(this.configJson());
    this.selectedModuleKey.set(this.model.moduleKey);
    this.updateSurfaceWarning();
  }

  previewText(): string {
    if (!this.model.moduleKey.trim() || !this.model.surfaceKey.trim()) {
      return '';
    }

    return this.t('workflow.designer.moduleSurface.preview')
      .replace('{moduleKey}', this.model.moduleKey.trim())
      .replace('{surfaceKey}', this.model.surfaceKey.trim());
  }

  onModuleKeyChanged(): void {
    this.selectedModuleKey.set(this.model.moduleKey);
    const surfaces = this.surfaceRegistry.listByModule(this.model.moduleKey);
    if (surfaces.length === 1) {
      this.model.surfaceKey = surfaces[0].surfaceKey;
    } else if (!surfaces.some(item => item.surfaceKey === this.model.surfaceKey)) {
      this.model.surfaceKey = '';
    }
    this.emitConfig();
  }

  onContextMappingChanged(mapping: Record<string, string>): void {
    this.model = { ...this.model, contextMapping: mapping };
    this.emitConfig();
  }

  onSurfaceChanged(): void {
    const registration = this.surfaceRegistry.resolve(this.model.moduleKey, this.model.surfaceKey);
    if (registration?.fullPageRouteTemplate && !this.model.fullPageRouteTemplate.trim()) {
      this.model.fullPageRouteTemplate = registration.fullPageRouteTemplate;
    }
    this.emitConfig();
  }

  emitConfig(): void {
    this.updateSurfaceWarning();
    this.configJsonChange.emit(buildModuleSurfaceConfigurationJson(this.model));
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private updateSurfaceWarning(): void {
    const moduleKey = this.model.moduleKey.trim();
    const surfaceKey = this.model.surfaceKey.trim();
    if (!moduleKey || !surfaceKey) {
      this.surfaceWarning.set(null);
      return;
    }

    if (!this.surfaceRegistry.resolve(moduleKey, surfaceKey)) {
      this.surfaceWarning.set(this.t('workflow.designer.moduleSurface.unregisteredWarning'));
      return;
    }

    this.surfaceWarning.set(null);
  }
}
